/** @jsxRuntime automatic */
/** @jsxImportSource preact */
// The lead's links (PJ 2026-10-09), on the lead card and on the Leads page: Source, Website or a muted "No website on file" tag,
// Google profile or a muted "No Google link on file" tag plus "Find on Google Maps" (a Maps search by name and address),
// and "Search the area" (Google: "<niche plural> near <city>, <state>"). PhoneKindNote is the one line about the number
// we dial (migration 0028). Hook-free, so tests/fd-lead-links.test.ts renders them; the pragmas above let Vitest compile it.
import type { Business } from '../../../src/shared/api';
import { businessNumbers, listingFieldsOf } from '../../../src/shared/api-core';
import { LinkOut } from '../components/ui-extra';
import {
  areaSearchQuery, areaSearchUrl, FIND_ON_MAPS_TITLE, googleProfileSlot, NO_PROFILE_TITLE, NO_WEBSITE_TITLE, ownerNumberLine, PHONE_KIND_TITLE,
  phoneKindLine,
} from './links';

/** Link row items, in a fragment: the caller keeps its own row (and adds e.g. "Email source" after). `niche`: the
 *  campaign's niche first, else the business's (pickNiche). */
export function LeadLinks(props: { business: Business; niche: string | null }) {
  const b = props.business;
  const { address } = listingFieldsOf(b);
  const place = { name: b.name, address, city: b.city, stateProv: b.stateProv, country: b.country };
  const slot = googleProfileSlot(b.gbpUrl, place);
  const area = { niche: props.niche, city: b.city, stateProv: b.stateProv, country: b.country };
  const areaUrl = areaSearchUrl(area);
  return (
    <>
      <LinkOut href={b.sourceUrl} title="Where this lead came from">Source</LinkOut>
      {b.website ? <LinkOut href={b.website}>Website</LinkOut> : <span class="chip chip-muted" title={NO_WEBSITE_TITLE}>No website on file</span>}
      {slot.kind === 'profile' ? <LinkOut href={slot.href}>Google profile</LinkOut> : <span class="chip chip-muted" title={NO_PROFILE_TITLE}>No Google link on file</span>}
      {slot.kind === 'search' ? <LinkOut href={slot.href} title={FIND_ON_MAPS_TITLE}>Find on Google Maps</LinkOut> : null}
      {areaUrl ? <LinkOut href={areaUrl} title={`Google search: ${areaSearchQuery(area)}`}>Search the area</LinkOut> : null}
    </>
  );
}

/** "Store line (matches Google)" / "Likely the owner's cell — Google lists (604) 555-0100 for the store"; nothing when
 *  the kind is unknown. Google's number is plain text, never a link (the dialer's Call the store, A, dials it, PJ
 *  2026-10-23). A business with the owner's number from the licence (0029) shows that first. */
export function PhoneKindNote(props: { business: Business }) {
  const f = listingFieldsOf(props.business);
  const line = phoneKindLine(f.phoneKind, f.googlePhoneE164);
  const owner = businessNumbers(props.business).owner;
  const extra = owner && !owner.isMain ? owner : null;
  if (!line && !extra) return null;
  return (
    <>
      {extra ? <p class="vd-phone-kind vd-phone-kind-warn" title={PHONE_KIND_TITLE}>{ownerNumberLine(extra)}</p> : null}
      {line ? <p class={`vd-phone-kind vd-phone-kind-${line.tone}`} title={PHONE_KIND_TITLE}>{line.text}</p> : null}
    </>
  );
}
